/**
 * Sesión Firebase, rol, permissions, fetchWithAuth e impersonación.
 */
import React, { createContext, useState, useCallback, useEffect, useContext, useRef } from 'react';
import { auth } from './firebaseConfig';
import { onAuthStateChanged, signOut, setPersistence, browserLocalPersistence } from "firebase/auth";
import { ProcessingOverlay } from './components/admin/studioUi';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

const isSilentMutationUrl = (url) => /log-view|update-last-login|log-login|\/notifications\/[^/]+\/read|notifications\/read-all|avatar-world\/presence/i.test(String(url || ''));

const busyLabelFor = (url, method, custom) => {
    if (custom) return custom;
    const u = String(url || '');
    const m = String(method || 'GET').toUpperCase();
    if (u.includes('/approve')) return 'Guardando aprobación…';
    if (u.includes('/comments') && m === 'DELETE') return 'Eliminando comentarios…';
    if (u.includes('/comments')) return 'Publicando comentario…';
    if (u.includes('/adjustments')) return 'Guardando ajuste…';
    if (m === 'DELETE') return 'Eliminando…';
    return 'Procesando…';
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState('cliente');
    const [userData, setUserData] = useState(null);
    const [userPermissions, setUserPermissions] = useState({});
    const [loading, setLoading] = useState(true);
    const [impersonation, setImpersonation] = useState(null);
    const [originalUserContext, setOriginalUserContext] = useState(null);
    const [busyCount, setBusyCount] = useState(0);
    const [busyMessage, setBusyMessage] = useState('Procesando…');
    const busyRef = useRef(0);
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081';

    const beginBusy = useCallback((message) => {
        busyRef.current += 1;
        if (message) setBusyMessage(message);
        setBusyCount(busyRef.current);
    }, []);

    const endBusy = useCallback(() => {
        busyRef.current = Math.max(0, busyRef.current - 1);
        setBusyCount(busyRef.current);
        if (busyRef.current === 0) setBusyMessage('Procesando…');
    }, []);

    const withBusy = useCallback(async (fn, message = 'Procesando…') => {
        beginBusy(message);
        try {
            return await fn();
        } finally {
            endBusy();
        }
    }, [beginBusy, endBusy]);

    /** fetch al backend Node con Bearer del usuario Firebase actual. */
    const fetchWithAuth = useCallback(async (url, options = {}, authFirebaseUser = user) => {
        if (!authFirebaseUser) {
            throw new Error("User not authenticated.");
        }
        const { silent = false, busyMessage: requestBusyMessage, ...fetchOptions } = options;
        const method = String(fetchOptions.method || 'GET').toUpperCase();
        const showBusy = MUTATING.has(method) && !silent && !isSilentMutationUrl(url);

        const token = await authFirebaseUser.getIdToken();
        const headers = {
            ...fetchOptions.headers,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
        if (showBusy) beginBusy(busyLabelFor(url, method, requestBusyMessage));
        try {
            const response = await fetch(url, { ...fetchOptions, headers });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
                throw new Error(errorData.error || errorData.message || 'API request failed');
            }
            return response;
        } finally {
            if (showBusy) endBusy();
        }
    }, [user, beginBusy, endBusy]);

    const fetchUserData = useCallback(async (currentUser) => {
        if (!currentUser) return;
        try {
            const res = await fetchWithAuth(`${API_URL}/user-role/${currentUser.uid}`, {}, currentUser);
            const data = await res.json();
            setUserData(data);
            setUserRole(data.rol || 'cliente');
            setUserPermissions(data.permissions || {});
        } catch (err) {
            console.error("Error fetching user data:", err);
            setUserRole('cliente');
            setUserPermissions({});
        } finally {
            setLoading(false);
        }
    }, [API_URL, fetchWithAuth]);

    useEffect(() => {
        setPersistence(auth, browserLocalPersistence).catch(() => {});
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                await fetchUserData(currentUser);
                // Always update last login time on any authenticated load
                fetchWithAuth(`${API_URL}/user/update-last-login/${currentUser.uid}`, { method: 'POST' }, currentUser).catch(err => console.error("Failed to update last login:", err));

                // Only log the login event if it's a new sign-in, not a session restoration
                if (sessionStorage.getItem('isNewLogin') === 'true') {
                    sessionStorage.removeItem('isNewLogin'); // Clear the flag immediately
                    fetchWithAuth(`${API_URL}/api/stats/users/log-login`, { method: 'POST' }, currentUser).catch(err => console.error("Failed to log login:", err));
                }

            } else {
                setUserData(null);
                setUserRole('cliente');
                setUserPermissions({});
                setImpersonation(null);
                setOriginalUserContext(null);
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, [fetchUserData, fetchWithAuth, API_URL]);

    const handleSignOut = () => {
        signOut(auth);
    };

    const startImpersonation = async (targetUser) => {
        if (!userPermissions.users_impersonate) return;
        if (window.confirm(`¿Quieres ver la aplicación como ${targetUser.correo}?`)) {
            setOriginalUserContext({ userData, userPermissions, userRole });
            const res = await fetchWithAuth(`${API_URL}/user-role/${targetUser.uid}`);
            const impersonatedData = await res.json();
            setUserData(impersonatedData);
            setUserPermissions(impersonatedData.permissions || {});
            setUserRole(impersonatedData.rol || 'cliente');
            setImpersonation({ uid: impersonatedData.uid, email: impersonatedData.correo, rol: impersonatedData.rol });
        }
    };

    const stopImpersonation = () => {
        if (window.confirm("¿Quieres volver a tu cuenta de administrador?")) {
            if (originalUserContext) {
                setUserData(originalUserContext.userData);
                setUserPermissions(originalUserContext.userPermissions);
                setUserRole(originalUserContext.userRole);
            }
            setImpersonation(null);
            setOriginalUserContext(null);
        }
    };

    const value = {
        user,
        userData,
        userRole,
        userPermissions,
        loading,
        setLoading,
        impersonation,
        API_URL,
        fetchWithAuth,
        withBusy,
        handleSignOut,
        startImpersonation,
        stopImpersonation,
        onUserDataUpdate: (fields) => setUserData(prev => ({ ...prev, ...fields })),
        fetchUserData
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
            {busyCount > 0 ? <ProcessingOverlay message={busyMessage} /> : null}
        </AuthContext.Provider>
    );
};